import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceFraudAlertRecord } from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-fraud-alert-banner',
  imports: [ButtonModule, AinStateComponent],
  template: `
    @if (loading()) {
      <ain-state type="loading" [title]="t('common.loading')" />
    } @else if (permissionDenied()) {
      <ain-state type="error" [title]="t('insurance.states.noPermission')" />
    } @else if (alert()) {
      <section class="banner" [class.high]="alert()!.severity === 'High'">
        <div>
          <strong>{{ t('insurance.ai.fraud.title') }}</strong>
          <p>{{ summaryFor(alert()!) }}</p>
          <small>{{ t('insurance.ai.fraud.severity') }}: {{ alert()!.severity }}</small>
        </div>
        <div class="actions">
          <button pButton type="button" size="small" severity="secondary" [label]="t('insurance.ai.fraud.dismiss')" (click)="dismiss()"></button>
          <button pButton type="button" size="small" severity="danger" [label]="t('insurance.ai.fraud.escalate')" (click)="escalate()"></button>
        </div>
      </section>
    }
  `,
  styles: [`
    .banner { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; border: 1px solid #f59e0b; background: #fffbeb; border-radius: 10px; padding: 12px; margin-bottom: 12px; }
    .banner.high { border-color: #ef4444; background: #fef2f2; }
    p { margin: 4px 0; font-size: 13px; }
    small { color: #6b7280; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; }
  `]
})
export class InsuranceFraudAlertBannerComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly localization = inject(LocalizationService);

  readonly claimId = input<string | null | undefined>(null);
  readonly quoteId = input<string | null | undefined>(null);
  readonly changed = output<void>();

  readonly loading = signal(false);
  readonly permissionDenied = signal(false);
  readonly alert = signal<InsuranceFraudAlertRecord | null>(null);

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    const claimId = this.claimId();
    const quoteId = this.quoteId();
    if (!claimId && !quoteId) return;
    this.loading.set(true);
    this.permissionDenied.set(false);
    this.api.listFraudAlerts({
      claimId: claimId ?? undefined,
      quoteId: quoteId ?? undefined,
      status: 'Open'
    }).subscribe({
      next: rows => {
        this.alert.set(rows[0] ?? null);
        this.loading.set(false);
      },
      error: err => {
        this.alert.set(null);
        this.loading.set(false);
        if (err?.status === 403) this.permissionDenied.set(true);
      }
    });
  }

  dismiss(): void {
    const id = this.alert()?.id;
    if (!id) return;
    this.api.dismissFraudAlert(id).subscribe({
      next: () => {
        this.alert.set(null);
        this.changed.emit();
      }
    });
  }

  escalate(): void {
    const id = this.alert()?.id;
    if (!id) return;
    this.api.escalateFraudAlert(id).subscribe({
      next: row => {
        this.alert.set(row);
        this.changed.emit();
      }
    });
  }

  summaryFor(row: InsuranceFraudAlertRecord): string {
    try {
      const parsed = JSON.parse(row.explanationJson) as { summary?: string };
      return parsed.summary || this.t('insurance.ai.fraud.defaultExplanation');
    } catch {
      return this.t('insurance.ai.fraud.defaultExplanation');
    }
  }

  t(key: string): string { return this.localization.translate(key); }
}
